<template>
  <aside class="fixed inset-x-0 bottom-0 z-40 max-h-[72vh] w-full rounded-t-2xl border-t border-line bg-bg2 shadow-2xl overflow-y-auto
    md:static md:z-auto md:max-h-none md:w-[22rem] md:rounded-none md:border-t-0 md:border-l md:bg-bg2/70 md:shadow-none">

    <!-- collapsed bar (mobile only) — tap to reopen the full sheet -->
    <button v-if="store.sheetCollapsed" @click="store.sheetCollapsed = false"
      class="md:hidden w-full flex items-center gap-2.5 px-5 py-3 text-left">
      <span class="w-2.5 h-2.5 rounded-full shrink-0" :style="{ background: berryColor(grape.color) }" />
      <span class="font-display text-lg leading-none">{{ grape.name }}</span>
      <span class="text-xs text-ink-faint ml-auto">details ↑</span>
      <span @click.stop="selectGrape(null)"
        class="w-7 h-7 rounded-full border border-line grid place-items-center text-ink-faint text-sm">✕</span>
    </button>

    <div class="p-5" :class="store.sheetCollapsed ? 'hidden md:block' : ''">
      <div class="flex items-start justify-between gap-2">
        <div>
          <p class="text-[0.65rem] uppercase tracking-[0.18em]" :style="{ color: berryColor(grape.color) }"
            :title="'berry colour — the wine\'s colour depends on winemaking'">
            {{ grape.ancestor_only ? "ancestral variety" : berryLabel(grape.color) + " grape" }}
          </p>
          <h2 class="font-display text-3xl leading-tight mt-0.5">{{ grape.name }}</h2>
          <p class="text-xs text-ink-faint mt-1">
            {{ [grape.origin_region, grape.origin_country].filter(Boolean).join(", ") }}
          </p>
        </div>
        <div class="flex gap-1 shrink-0">
          <button v-if="!grape.ancestor_only" @click="toggleLike(grape.slug)" :title="liked ? 'Unlike' : 'I love this grape'"
            class="w-9 h-9 rounded-full border grid place-items-center text-lg transition-colors"
            :class="liked ? 'border-wine bg-wine/25 text-wine-bright' : 'border-line text-ink-faint hover:text-wine-bright'">
            {{ liked ? "♥" : "♡" }}
          </button>
          <button @click="selectGrape(null)" class="w-9 h-9 rounded-full border border-line grid place-items-center text-ink-faint hover:text-ink">✕</button>
        </div>
      </div>

      <p v-if="grape.synonyms?.length" class="text-xs text-ink-dim mt-2 italic">
        a.k.a. {{ grape.synonyms.join(", ") }}
      </p>

      <p class="text-sm text-ink-dim leading-relaxed mt-4">{{ grape.blurb }}</p>

      <!-- explore this grape across the views -->
      <div class="mt-4">
        <p class="text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint mb-2">See {{ grape.name }} in</p>
        <div class="flex flex-wrap gap-1.5">
          <button v-for="v in exploreTabs" :key="v.id" @click="exploreView(v.id)"
            class="px-3 py-1.5 rounded-full text-sm border transition-colors"
            :class="store.view === v.id
              ? 'border-wine/50 bg-wine/25 text-ink'
              : 'border-line text-ink-dim hover:text-ink hover:bg-wine/10'">
            {{ v.label }}
          </button>
        </div>
      </div>

      <!-- lineage strip -->
      <div v-if="grape.parent1_slug || grape.parent2_slug" class="mt-4 panel px-3 py-2.5">
        <p class="text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint mb-1.5">Parentage</p>
        <div class="flex items-center gap-2 text-sm flex-wrap">
          <button v-for="p in parents" :key="p" @click="jumpTo(p)" class="chip hover:bg-wine/15 cursor-pointer">
            {{ bySlug.get(p)?.name || p }}
          </button>
        </div>
      </div>

      <!-- profile -->
      <div v-if="grape.profile" class="mt-5">
        <p class="text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint mb-2">Structure</p>
        <div v-for="dim in dims" :key="dim.key" class="flex items-center gap-3 mb-1.5">
          <span class="text-xs text-ink-dim w-24 shrink-0">{{ dim.label }}</span>
          <div class="flex-1 h-1.5 rounded-full bg-card overflow-hidden">
            <div class="h-full rounded-full" :style="{
              width: (grape.profile[dim.key] / 5 * 100) + '%',
              background: grape.color === 'red' ? 'linear-gradient(90deg,#7d2d3a,#d9636f)'
                : grape.color === 'gris' ? 'linear-gradient(90deg,#8a5540,#c98a6d)'
                : 'linear-gradient(90deg,#8a7a30,#d9c778)'
            }" />
          </div>
          <span class="text-[0.65rem] text-ink-faint w-4 text-right">{{ grape.profile[dim.key] }}</span>
        </div>
      </div>

      <!-- descriptors -->
      <div v-if="grape.descriptors?.length" class="mt-5">
        <p class="text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint mb-2">Tastes like</p>
        <div class="flex flex-wrap gap-1.5">
          <span v-for="d in grape.descriptors" :key="d" class="chip">{{ pretty(d) }}</span>
        </div>
      </div>

      <!-- compounds -->
      <div v-if="grapeCompounds.length" class="mt-5">
        <p class="text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint mb-2">Signature chemistry</p>
        <div class="flex flex-col gap-1.5">
          <div v-for="c in grapeCompounds" :key="c.slug" class="panel px-3 py-2">
            <p class="text-sm leading-snug">{{ c.name }}</p>
            <p class="text-xs text-gold-bright/80 italic">“{{ c.aroma }}”</p>
          </div>
        </div>
      </div>

      <!-- nearest neighbours -->
      <div v-if="neighbours.length" class="mt-5">
        <p class="text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint mb-2">Closest in flavour</p>
        <div class="flex flex-wrap gap-1.5">
          <button v-for="n in neighbours" :key="n.slug" @click="selectGrape(n.slug)"
            class="chip hover:bg-wine/15 cursor-pointer">
            {{ bySlug.get(n.slug)?.name }} <span class="text-ink-faint ml-1">{{ Math.round(n.weight * 100) }}%</span>
          </button>
        </div>
      </div>

      <div v-if="grape.regions?.length" class="mt-5">
        <p class="text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint mb-2">Where it shines</p>
        <p class="text-sm text-ink-dim">{{ grape.regions.join(" · ") }}</p>
      </div>

      <div v-if="grape.example_wines?.length" class="mt-4">
        <p class="text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint mb-2">Bottles to look for</p>
        <p class="text-sm text-ink-dim italic">{{ grape.example_wines.join(" · ") }}</p>
      </div>

      <button @click="ask" class="mt-6 w-full py-2.5 rounded-full border border-gold/50 text-gold-bright text-sm hover:bg-gold/10 transition-colors">
        ✦ Ask the sommelier about {{ grape.name }}
      </button>
    </div>
  </aside>
</template>

<script setup>
import { computed } from "vue"
import { store, bySlug, selectGrape, exploreView, toggleLike, askClaude, berryColor, berryLabel } from "../store"

const props = defineProps({ grape: Object })

const exploreTabs = [
  { id: "network", label: "Network" },
  { id: "lineage", label: "Lineage" },
  { id: "wheel", label: "Flavour wheel" },
  { id: "compounds", label: "Compounds" },
]

const dims = [
  { key: "body", label: "Body" },
  { key: "tannin", label: "Tannin" },
  { key: "acidity", label: "Acidity" },
  { key: "sweetness", label: "Sweetness" },
  { key: "aromatic_intensity", label: "Aromatics" },
]

const liked = computed(() => store.likes.has(props.grape.slug))
const parents = computed(() => [props.grape.parent1_slug, props.grape.parent2_slug].filter(Boolean))

const grapeCompounds = computed(() =>
  (props.grape.compounds || []).map(s => store.compounds.find(c => c.slug === s)).filter(Boolean))

const neighbours = computed(() =>
  store.edges
    .filter(e => e.source === props.grape.slug || e.target === props.grape.slug)
    .map(e => ({ slug: e.source === props.grape.slug ? e.target : e.source, weight: e.weight }))
    .sort((a, b) => b.weight - a.weight)
    .slice(0, 6))

const pretty = s => s.replace(/-/g, " ")

function jumpTo(slug) { if (bySlug.value.get(slug)) selectGrape(slug) }
function ask() {
  askClaude(`Tell me about ${props.grape.name} — what makes it distinctive, and what should I taste to understand it?`)
}
</script>
